/* eslint-disable @typescript-eslint/no-unused-vars */
// Interfaces
import {
  IdcardOutlined,
  GoldOutlined,
  UnorderedListOutlined,
  PlusOutlined,
  HomeOutlined,
  FileSearchOutlined,
  SolutionOutlined,
  CompassOutlined,
  FileTextOutlined,
  CreditCardOutlined
} from '@ant-design/icons';

// Constants
import { PRIMARY_COLOR } from '../../../constants/colors';
import { PERMISSIONS } from '../../../constants/permissions';
import { ROUTES } from '../../../constants/routes-constants';

// Interfaces
import type { IUser } from '../../../interfaces/user';
import type { ProLayoutProps } from '@ant-design/pro-layout';

const getProductoresMinerosRoutes = (
  _isMobile: boolean,
  permissions: string[]
): NonNullable<ProLayoutProps['route']> | null => {
  return {
    key: 'productores-mineros',
    name: 'Productores Mineros',
    icon: <GoldOutlined style={{ color: PRIMARY_COLOR }} />,

    children: [
      permissions.includes(PERMISSIONS.PRODUCTORES_MINEROS_LIST)
        ? {
            key: 'productores-mineros-listado',
            path: ROUTES.ADMIN_PRODUCTORES_ROUTE,
            name: 'Listado de productores mineros',
            icon: <UnorderedListOutlined />
          }
        : undefined,
      permissions.includes(PERMISSIONS.PRODUCTORES_MINEROS_CREATE)
        ? {
            key: 'productores-mineros-alta',
            path: ROUTES.ADMIN_PRODUCTORES_ALTA_ROUTE,
            name: 'Alta de productor minero',
            icon: <PlusOutlined />
          }
        : undefined
    ].filter(Boolean) as NonNullable<ProLayoutProps['route']>[]
  };
};

const getNoProductoresRoutes = (
  _isMobile: boolean,
  permissions: string[]
): NonNullable<ProLayoutProps['route']> | null => {
  return {
    key: 'no-productores',
    name: 'No Productores',
    icon: <IdcardOutlined style={{ color: PRIMARY_COLOR }} />,
    children: [
      permissions.includes(PERMISSIONS.NO_PRODUCTORES_LIST)
        ? {
            key: 'no-productores-listado',
            path: ROUTES.ADMIN_NO_PRODUCTORES_ROUTE,
            name: 'Listado de no productores',
            icon: <UnorderedListOutlined />
          }
        : undefined,
      permissions.includes(PERMISSIONS.NO_PRODUCTORES_CREATE)
        ? {
            key: 'no-productores-alta',
            path: ROUTES.ADMIN_NO_PRODUCTORES_ALTA_ROUTE,
            name: 'Alta de no productor',
            icon: <PlusOutlined />
          }
        : undefined
    ].filter(Boolean) as NonNullable<ProLayoutProps['route']>[]
  };
};

const getEstablecimientosRoutes = (
  _isMobile: boolean,
  permissions: string[]
): NonNullable<ProLayoutProps['route']> | null => {
  return {
    key: 'establecimientos',
    name: 'Establecimientos',
    icon: <HomeOutlined style={{ color: PRIMARY_COLOR }} />,
    children: [
      permissions.includes(PERMISSIONS.ESTABLECIMIENTO_MINERO_LIST)
        ? {
            key: 'establecimientos-mineros',
            path: `${ROUTES.ADMIN_ESTABLECIMIENTOS_MINEROS_ROUTE}?tab=mineros`,
            name: 'Establecimientos mineros',
            icon: <UnorderedListOutlined />
          }
        : undefined,
      permissions.includes(PERMISSIONS.ESTABLECIMIENTO_MINERO_CREATE)
        ? {
            key: 'establecimientos-mineros-alta',
            path: ROUTES.ADMIN_ESTABLECIMIENTOS_MINEROS_ALTA_ROUTE,
            name: 'Alta establecimiento minero',
            icon: <PlusOutlined />
          }
        : undefined,
      permissions.includes(PERMISSIONS.ESTABLECIMIENTO_NO_PRODUCTOR_LIST)
        ? {
            key: 'establecimientos-no-mineros',
            path: `${ROUTES.ADMIN_ESTABLECIMIENTOS_MINEROS_ROUTE}?tab=no-mineros`,
            name: 'Establecimientos no mineros',
            icon: <UnorderedListOutlined />
          }
        : undefined,
      permissions.includes(PERMISSIONS.ESTABLECIMIENTO_NO_PRODUCTOR_CREATE)
        ? {
            key: 'establecimientos-no-mineros-alta',
            path: ROUTES.ADMIN_ESTABLECIMIENTOS_NO_MINEROS_ALTA_ROUTE,
            name: 'Alta establecimiento no minero',
            icon: <PlusOutlined />
          }
        : undefined,
      permissions.includes(PERMISSIONS.CONSECION_MINERA_LIST)
        ? {
            key: 'establecimientos-padrones-de-minas',
            path: `${ROUTES.ADMIN_ESTABLECIMIENTOS_MINEROS_ROUTE}?tab=padrones`,
            name: 'Establecimientos conceción de minas',
            icon: <UnorderedListOutlined />
          }
        : undefined,
      permissions.includes(PERMISSIONS.CONSECION_MINERA_CREATE)
        ? {
            key: 'establecimientos-padrones-de-minas-alta',
            path: ROUTES.ADMIN_CONCESIONES_MINERAS_ALTA_ROUTE,
            name: 'Alta establecimiento conceción de mina',
            icon: <PlusOutlined />
          }
        : undefined
    ].filter(Boolean) as NonNullable<ProLayoutProps['route']>[]
  };
};

const getPadronesDeMinasRoutes = (
  _isMobile: boolean,
  permissions: string[]
): NonNullable<ProLayoutProps['route']> | null => {
  return {
    key: 'padrones-de-minas',
    name: 'Concesionarios de Minas',
    icon: <SolutionOutlined style={{ color: PRIMARY_COLOR }} />,
    children: [
      permissions.includes(PERMISSIONS.PADRON_DE_MINAS_LIST)
        ? {
            key: 'padrones-de-minas-listado',
            path: ROUTES.ADMIN_PADRONES_DE_MINAS_ROUTE,
            name: 'Listado de concesionarios de minas',
            icon: <SolutionOutlined />
          }
        : undefined,
      permissions.includes(PERMISSIONS.PADRON_DE_MINAS_CREATE)
        ? {
            key: 'padrones-de-minas-alta',
            path: ROUTES.ADMIN_PADRONES_DE_MINAS_ALTA_ROUTE,
            name: 'Alta de concesionario de minas',
            icon: <PlusOutlined />
          }
        : undefined
    ].filter(Boolean) as NonNullable<ProLayoutProps['route']>[]
  };
};

const getCateosRoutes = (
  _isMobile: boolean,
  permissions: string[]
): NonNullable<ProLayoutProps['route']> | null => {
  return {
    key: 'cateos',
    name: 'Cateos',
    icon: <FileSearchOutlined style={{ color: PRIMARY_COLOR }} />,
    children: [
      permissions.includes(PERMISSIONS.CATEOS_LIST)
        ? {
            key: 'cateos-listado',
            path: ROUTES.ADMIN_CATEOS_ROUTE,
            name: 'Listado de cateos',
            icon: <UnorderedListOutlined />
          }
        : undefined,
      permissions.includes(PERMISSIONS.CATEOS_CREATE)
        ? {
            key: 'cateos-alta',
            path: ROUTES.ADMIN_CATEOS_ALTA_ROUTE,
            name: 'Alta de cateo',
            icon: <PlusOutlined />
          }
        : undefined
    ].filter(Boolean) as NonNullable<ProLayoutProps['route']>[]
  };
};

const getBuquesRoutes = (
  _isMobile: boolean,
  permissions: string[]
): NonNullable<ProLayoutProps['route']> | null => {
  return {
    key: 'buques',
    name: 'Buques',
    icon: <CompassOutlined style={{ color: PRIMARY_COLOR }} />,
    children: [
      permissions.includes(PERMISSIONS.TRANSPORTE_BUQUE_LIST)
        ? {
            key: 'buques-listado',
            path: ROUTES.ADMIN_BUQUES_ROUTE,
            name: 'Listado de buques',
            icon: <UnorderedListOutlined />
          }
        : undefined,
      permissions.includes(PERMISSIONS.TRANSPORTE_BUQUE_CREATE)
        ? {
            key: 'buques-alta',
            path: ROUTES.ADMIN_BUQUES_ALTA_ROUTE,
            name: 'Alta de buque',
            icon: <PlusOutlined />
          }
        : undefined
    ].filter(Boolean) as NonNullable<ProLayoutProps['route']>[]
  };
};

const getGuiasRoutes = (
  _isMobile: boolean,
  permissions: string[]
): NonNullable<ProLayoutProps['route']> | null => {
  return {
    key: 'guias',
    name: 'Guías',
    icon: <FileTextOutlined style={{ color: PRIMARY_COLOR }} />,
    children: [
      permissions.includes(PERMISSIONS.GUIA_LIST)
        ? {
            key: 'guias-listado',
            path: ROUTES.ADMIN_GUIAS_ROUTE,
            name: 'Listado de guías emitidas',
            icon: <UnorderedListOutlined />
          }
        : undefined
    ].filter(Boolean) as NonNullable<ProLayoutProps['route']>[]
  };
};

const getComprobantesRoutes = (
  _isMobile: boolean,
  permissions: string[]
): NonNullable<ProLayoutProps['route']> | null => {
  return {
    key: 'comprobantes',
    name: 'Comprobantes',
    icon: <CreditCardOutlined style={{ color: PRIMARY_COLOR }} />,
    children: [
      {
        key: 'comprobantes-listado',
        path: ROUTES.ADMIN_COMPROBANTES_ROUTE,
        name: 'Listado de comprobantes',
        icon: <UnorderedListOutlined />
      }
    ].filter(Boolean) as NonNullable<ProLayoutProps['route']>[]
  };
};

type MenuNode = {
  key?: string;
  path?: string;
  children?: (MenuNode | undefined)[];
};

const pathWithoutQuery = (path: string) => path.split('?')[0].split('#')[0];

const isPathPrefix = (base: string, pathname: string) =>
  pathname.startsWith(base.endsWith('/') ? base : `${base}/`);

const pathMatchesLocation = (itemPath: string, pathname: string) => {
  const base = pathWithoutQuery(itemPath);
  return Boolean(base) && (pathname === base || isPathPrefix(base, pathname));
};

/** ProLayout marca activo todo path que sea prefijo. Quedarse con el más específico. */
export const getActiveMenuKey = (
  menuRoutes: readonly (MenuNode | undefined)[] | undefined,
  pathname: string,
  search = ''
): string | undefined => {
  const matches: { key: string; path: string; base: string }[] = [];

  const walk = (items?: readonly (MenuNode | undefined)[]) => {
    items?.forEach((item) => {
      if (!item) return;
      if (item.path && item.key && pathMatchesLocation(item.path, pathname)) {
        matches.push({ key: item.key, path: item.path, base: pathWithoutQuery(item.path) });
      }
      if (item.children?.length) walk(item.children);
    });
  };

  walk(menuRoutes);
  if (!matches.length) return undefined;

  const specific = matches.filter(
    (item) => !matches.some((other) => item.base !== other.base && isPathPrefix(item.base, other.base))
  );

  const queryMatch = specific.find((item) => {
    const queryIndex = item.path.indexOf('?');
    return queryIndex !== -1 && item.path.slice(queryIndex) === search;
  });
  if (queryMatch) return queryMatch.key;

  if (specific.length === 1) return specific[0].key;

  const withoutQuery = specific.filter((item) => !item.path.includes('?'));
  if (withoutQuery.length === 1) return withoutQuery[0].key;

  return specific[0]?.key;
};

export default (_isMobile: boolean, _user: IUser) => {
  const routes: NonNullable<ProLayoutProps['route']>[] = [];

  const permissions =
    (_user && _user?.roles.map((r) => r.permissions.map((p) => p.descripcion)).flat()) || [];

  // Productores Mineros
  const productoresMinerosRoutes = getProductoresMinerosRoutes(_isMobile, permissions);
  if (productoresMinerosRoutes) routes.push(productoresMinerosRoutes);

  // Titulares No Productores
  const noProductoresRoutes = getNoProductoresRoutes(_isMobile, permissions);
  if (noProductoresRoutes) routes.push(noProductoresRoutes);

  // Padrones de Mineras
  const padronesDeMinasRoutes = getPadronesDeMinasRoutes(_isMobile, permissions);
  if (padronesDeMinasRoutes) routes.push(padronesDeMinasRoutes);

  // Establecimientos
  const establecimientosRoutes = getEstablecimientosRoutes(_isMobile, permissions);
  if (establecimientosRoutes) routes.push(establecimientosRoutes);

  // Buques
  const buquesRoutes = getBuquesRoutes(_isMobile, permissions);
  if (buquesRoutes) routes.push(buquesRoutes);

  // Cateos
  const cateosRoutes = getCateosRoutes(_isMobile, permissions);
  if (cateosRoutes) routes.push(cateosRoutes);

  // Guías
  const guiasRoutes = getGuiasRoutes(_isMobile, permissions);
  if (guiasRoutes) routes.push(guiasRoutes);

  // // Comprobantes
  // const comprobantesRoutes = getComprobantesRoutes(_isMobile, permissions);
  // if (comprobantesRoutes) routes.push(comprobantesRoutes);

  return {
    routes
  };
};
